/* eslint-disable */
"use client";

import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

/* ------------------------------ route context ------------------------------ */
type RouterValue = {
  path: string;
  navigate: (path: string) => void;
  refreshNonce: number;
  refresh: () => void;
};

const RouterContext = createContext<RouterValue>({
  path: "/",
  navigate: () => {},
  refreshNonce: 0,
  refresh: () => {},
});

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState("/");
  const [refreshNonce, setNonce] = useState(0);
  const navigate = useCallback((p: string) => {
    // strip any query string — server components handled those; we keep local filter state instead
    setPath((p || "/").split("?")[0]);
    window.scrollTo({ top: 0 });
  }, []);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  return (
    <RouterContext.Provider value={{ path, navigate, refreshNonce, refresh }}>
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter() {
  const ctx = useContext(RouterContext);
  return {
    push: (p: string) => ctx.navigate(p),
    replace: (_p: string) => {},
    refresh: () => ctx.refresh(),
  };
}

export function usePathname() {
  return useContext(RouterContext).path;
}

export function useRouterState() {
  return useContext(RouterContext);
}

export function Link({
  href,
  children,
  className,
  title,
}: {
  href: string;
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  const { navigate } = useContext(RouterContext);
  return (
    <a
      href={href}
      className={className}
      title={title}
      onClick={(e) => {
        e.preventDefault();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}

/* ------------------------------ filter context ------------------------------ */
type FilterStore = {
  values: Record<string, string>;
  set: (key: string, value: string) => void;
  reset: () => void;
};

const FiltersContext = createContext<FilterStore>({ values: {}, set: () => {}, reset: () => {} });

// Page wrappers own the filter state and provide it; the reused client
// components call useUrlFilters() which simply reads that shared store.
export function useFilterStore(initial: Record<string, string>): FilterStore {
  const [values, setValues] = useState<Record<string, string>>(initial);
  const set = useCallback((key: string, value: string) => setValues((v) => ({ ...v, [key]: value })), []);
  const reset = useCallback(
    () => setValues(Object.fromEntries(Object.keys(initial).map((k) => [k, ""]))),
    // eslint-disable-next-line
    []
  );
  return { values, set, reset };
}

export function FiltersProvider({ store, children }: { store: FilterStore; children: ReactNode }) {
  return <FiltersContext.Provider value={store}>{children}</FiltersContext.Provider>;
}

export function useUrlFilters<T extends Record<string, string>>(_initial: T) {
  const ctx = useContext(FiltersContext);
  return { values: ctx.values as T, set: ctx.set as (k: keyof T, v: string) => void, reset: ctx.reset };
}
